// @ts-ignore - vendored self-contained Three.js module.
import * as THREE from '../../vendor/three.module.min.js';
import { MaterialLibrary } from './MaterialLibrary.js';

type ParticleKind = 'shard' | 'cube';

interface Particle {
  kind: ParticleKind;
  position: any;
  velocity: any;
  rotation: any;
  spin: any;
  life: number;
  maxLife: number;
  gravity: number;
  grow: number;
  baseScale: number;
  color: number;
}

interface RingEffect {
  position: any;
  life: number;
  maxLife: number;
  speed: number;
  baseScale: number;
  color: number;
}

interface FlashEffect {
  position: any;
  life: number;
  maxLife: number;
  baseScale: number;
  color: number;
}

/**
 * Allocation-light combat effects.
 *
 * Four stable InstancedMesh surfaces replace per-hit mesh/material/PointLight
 * creation. Stable light and shader counts avoid compilation spikes during
 * firefights, while pooled state objects avoid garbage-collection hitches.
 */
export class VfxSystem {
  private readonly particles: Particle[] = [];
  private readonly particlePool: Particle[] = [];
  private readonly rings: RingEffect[] = [];
  private readonly ringPool: RingEffect[] = [];
  private readonly flashes: FlashEffect[] = [];
  private readonly flashPool: FlashEffect[] = [];

  private readonly shardGeometry = new THREE.TetrahedronGeometry(0.14, 0);
  private readonly cubeGeometry = new THREE.BoxGeometry(0.14, 0.14, 0.14);
  private readonly ringGeometry = new THREE.RingGeometry(0.45, 0.58, 20);
  private readonly flashGeometry = new THREE.SphereGeometry(0.32, 10, 6);

  private readonly shardInstances: any;
  private readonly cubeInstances: any;
  private readonly ringInstances: any;
  private readonly flashInstances: any;
  private readonly dummy = new THREE.Object3D();
  private readonly tempColor = new THREE.Color();

  private readonly maxParticles: number;
  private readonly maxRings: number;
  private readonly maxFlashes: number;

  constructor(private readonly scene: any, _materials: MaterialLibrary, private qualityScale = 1) {
    this.maxParticles = Math.max(32, Math.round(128 * qualityScale));
    this.maxRings = Math.max(8, Math.round(24 * qualityScale));
    this.maxFlashes = Math.max(6, Math.round(18 * qualityScale));

    // Allocate state during loading instead of on the first firefight frame.
    for (let i = 0; i < this.maxParticles; i += 1) this.particlePool.push(this.createParticleState());
    for (let i = 0; i < this.maxRings; i += 1) this.ringPool.push(this.createRingState());
    for (let i = 0; i < this.maxFlashes; i += 1) this.flashPool.push(this.createFlashState());

    const shardMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 0.92,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    const cubeMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      toneMapped: false,
    });
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 0.62,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    const flashMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      vertexColors: true,
      transparent: true,
      opacity: 0.42,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });

    this.shardInstances = this.createInstanceSurface(this.shardGeometry, shardMaterial, this.maxParticles);
    this.cubeInstances = this.createInstanceSurface(this.cubeGeometry, cubeMaterial, this.maxParticles);
    this.ringInstances = this.createInstanceSurface(this.ringGeometry, ringMaterial, this.maxRings);
    this.flashInstances = this.createInstanceSurface(this.flashGeometry, flashMaterial, this.maxFlashes);
    this.scene.add(this.shardInstances, this.cubeInstances, this.ringInstances, this.flashInstances);
  }

  private createInstanceSurface(geometry: any, material: any, capacity: number): any {
    const mesh = new THREE.InstancedMesh(geometry, material, capacity);
    // One zero-scale instance precompiles the shader during the menu render,
    // avoiding a first-shot compilation hitch. The first update sets count 0.
    mesh.count = capacity > 0 ? 1 : 0;
    mesh.frustumCulled = false;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    if (capacity > 0) {
      mesh.setMatrixAt(0, new THREE.Matrix4().makeScale(0, 0, 0));
      mesh.setColorAt(0, this.tempColor.setHex(0xffffff));
      if (mesh.instanceColor) mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    return mesh;
  }

  setQualityScale(value: number): void {
    this.qualityScale = THREE.MathUtils.clamp(value, 0.35, 1);
  }

  private scaledCount(value: number): number {
    return Math.max(1, Math.round(value * this.qualityScale));
  }

  muzzle(position: any, direction: any, color: number): void {
    for (let i = 0; i < this.scaledCount(6); i += 1) {
      this.spawnParticle(
        position,
        direction.x * (2.5 + Math.random() * 2) + (Math.random() - 0.5) * 1.6,
        0.8 + Math.random() * 1.7,
        direction.z * (2.5 + Math.random() * 2) + (Math.random() - 0.5) * 1.6,
        color,
        0.24 + Math.random() * 0.16,
        2.6,
        0.2,
        'shard',
        1,
      );
    }
    // 出膛亮芯：几粒高速短命的白热碎屑沿弹道前冲，强化「喷射」感。
    for (let i = 0; i < this.scaledCount(3); i += 1) {
      this.spawnParticle(
        position,
        direction.x * (6 + Math.random() * 3) + (Math.random() - 0.5) * 0.9,
        0.3 + Math.random() * 0.9,
        direction.z * (6 + Math.random() * 3) + (Math.random() - 0.5) * 0.9,
        0xfff6c9,
        0.14 + Math.random() * 0.08,
        1.2,
        0,
        'shard',
        0.8,
      );
    }
    this.flash(position, color, 1.9, 0.11);
    // 白核闪光叠在彩色主闪光上，亮部更「炸」。
    this.flash(position, 0xfffbe8, 0.95, 0.07);
    this.ring(position, color, 0.55, 6.5);
  }

  impact(position: any, color: number, strong = false): void {
    const count = this.scaledCount(strong ? 18 : 9);
    for (let i = 0; i < count; i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 2.5 + Math.random() * (strong ? 6 : 3.4);
      this.spawnParticle(
        position,
        Math.cos(angle) * speed,
        1 + Math.random() * 4.4,
        Math.sin(angle) * speed,
        color,
        0.28 + Math.random() * 0.35,
        7.5,
        0,
        i % 2 === 0 ? 'shard' : 'cube',
        1,
      );
    }
    this.ring(position, color, strong ? 1.5 : 0.8, strong ? 7.5 : 4.5);
    this.flash(position, color, strong ? 2.8 : 1.4, strong ? 0.22 : 0.1);
  }

  /**
   * 命中反馈分级：普通 / 暴击（强化弹、残血收割）/ 击杀。
   * 现有 impact 调用保持有效；新接线点可按伤害语境选档。
   */
  impactGraded(position: any, color: number, tier: 'normal' | 'critical' | 'kill'): void {
    if (tier === 'kill') {
      this.killBlast(position, color);
      return;
    }
    if (tier === 'critical') {
      this.impact(position, color, true);
      // 暴击追加：白热内闪 + 更快更亮的次级环，与普通命中拉开档次。
      this.flash(position, 0xfff3b0, 1.8, 0.16);
      this.ring(position, 0xffe89a, 0.65, 9);
      return;
    }
    this.impact(position, color, false);
  }

  /** 击杀专属爆发：爆炸 + 金色二次闪光 + 双环冲击 + 上窜亮屑喷泉。 */
  killBlast(position: any, color: number): void {
    this.explosion(position, color, 1.18);
    this.flash(position, 0xffd95e, 3.4, 0.3);
    this.ring(position, 0xffd95e, 2.8, 5.2);
    for (let i = 0; i < this.scaledCount(10); i += 1) {
      this.spawnParticle(
        position,
        (Math.random() - 0.5) * 2.4,
        5.5 + Math.random() * 4.5,
        (Math.random() - 0.5) * 2.4,
        i % 2 === 0 ? 0xffd95e : color,
        0.55 + Math.random() * 0.3,
        9.5,
        0,
        'shard',
        1.1,
      );
    }
  }

  explosion(position: any, color = 0xff824e, scale = 1): void {
    const hot = 0xffdd58;
    for (let i = 0; i < this.scaledCount(22 * scale); i += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (2.5 + Math.random() * 6) * scale;
      this.spawnParticle(
        position,
        Math.cos(angle) * speed,
        1.5 + Math.random() * 5.5 * scale,
        Math.sin(angle) * speed,
        i % 3 === 0 ? hot : color,
        0.4 + Math.random() * 0.45,
        8.5,
        0,
        'shard',
        0.55 + Math.random() * 0.7,
      );
    }
    for (let i = 0; i < this.scaledCount(8 * scale); i += 1) {
      this.spawnParticle(
        position,
        (Math.random() - 0.5) * 2.5,
        1.2 + Math.random() * 2.8,
        (Math.random() - 0.5) * 2.5,
        0x5e5364,
        0.7 + Math.random() * 0.45,
        0.8,
        1.5,
        'cube',
        0.8 + Math.random() * 0.7,
      );
    }
    this.ring(position, color, 2.2 * scale, 8);
    this.flash(position, 0xffb549, 4.2 * scale, 0.28);
  }

  pickup(position: any, color: number): void {
    const count = this.scaledCount(12);
    for (let i = 0; i < count; i += 1) {
      const angle = i / count * Math.PI * 2;
      this.spawnParticle(position, Math.cos(angle) * 3.2, 2.2 + Math.random() * 1.8, Math.sin(angle) * 3.2, color, 0.46, 5.2, 0, 'shard', 1);
    }
    this.ring(position, color, 1.25, 6);
    this.flash(position, color, 2, 0.2);
  }

  driveDust(position: any, velocityX: number, velocityZ: number, color: number, intensity = 1): void {
    const count = this.scaledCount(1 + Math.round(intensity * 2));
    for (let i = 0; i < count; i += 1) {
      this.spawnParticle(
        position,
        velocityX + (Math.random() - 0.5) * 1.4,
        0.18 + Math.random() * 0.42,
        velocityZ + (Math.random() - 0.5) * 1.4,
        color,
        0.32 + Math.random() * 0.18,
        1.2,
        1.3,
        'cube',
        0.5 + intensity * 0.35,
      );
    }
  }

  spawn(position: any, color: number): void {
    const count = this.scaledCount(10);
    for (let i = 0; i < count; i += 1) {
      const angle = i / count * Math.PI * 2;
      this.spawnParticle(position, Math.cos(angle) * 1.1, 2 + Math.random() * 1.2, Math.sin(angle) * 1.1, color, 0.58, 3, 0, 'cube', 1);
    }
    this.ring(position, color, 1.1, 4.5);
  }

  shieldHit(position: any, color: number): void {
    this.ring(position, color, 1.05, 7.5);
    this.flash(position, color, 2.2, 0.12);
  }

  crateBreak(position: any, color = 0xe59d59): void {
    for (let i = 0; i < this.scaledCount(12); i += 1) {
      const angle = Math.random() * Math.PI * 2;
      this.spawnParticle(
        position,
        Math.cos(angle) * (2 + Math.random() * 3),
        1 + Math.random() * 4,
        Math.sin(angle) * (2 + Math.random() * 3),
        color,
        0.55 + Math.random() * 0.45,
        8.4,
        0,
        'cube',
        1.1,
      );
    }
  }

  update(delta: number): void {
    for (let i = this.particles.length - 1; i >= 0; i -= 1) {
      const particle = this.particles[i];
      particle.life -= delta;
      if (particle.life <= 0) {
        this.releaseParticle(i);
        continue;
      }
      particle.velocity.y -= particle.gravity * delta;
      particle.position.addScaledVector(particle.velocity, delta);
      particle.rotation.x += particle.spin.x * delta;
      particle.rotation.y += particle.spin.y * delta;
      particle.rotation.z += particle.spin.z * delta;
      if (particle.position.y < 0.08) {
        particle.position.y = 0.08;
        particle.velocity.y *= -0.28;
        particle.velocity.x *= 0.76;
        particle.velocity.z *= 0.76;
      }
    }

    for (let i = this.rings.length - 1; i >= 0; i -= 1) {
      this.rings[i].life -= delta;
      if (this.rings[i].life <= 0) this.releaseRing(i);
    }
    for (let i = this.flashes.length - 1; i >= 0; i -= 1) {
      this.flashes[i].life -= delta;
      if (this.flashes[i].life <= 0) this.releaseFlash(i);
    }

    this.syncInstances();
  }

  clear(): void {
    while (this.particles.length > 0) this.releaseParticle(this.particles.length - 1);
    while (this.rings.length > 0) this.releaseRing(this.rings.length - 1);
    while (this.flashes.length > 0) this.releaseFlash(this.flashes.length - 1);
    this.syncInstances();
  }

  get activeCount(): number {
    return this.particles.length + this.rings.length + this.flashes.length;
  }

  get diagnostics(): Record<string, number> {
    return {
      particles: this.particles.length,
      rings: this.rings.length,
      flashes: this.flashes.length,
      maxParticles: this.maxParticles,
      drawSurfaces: 4,
    };
  }

  private acquireParticle(): Particle {
    return this.particlePool.pop() ?? this.createParticleState();
  }

  private createParticleState(): Particle {
    return {
      kind: 'shard',
      position: new THREE.Vector3(),
      velocity: new THREE.Vector3(),
      rotation: new THREE.Vector3(),
      spin: new THREE.Vector3(),
      life: 0,
      maxLife: 0,
      gravity: 0,
      grow: 0,
      baseScale: 1,
      color: 0xffffff,
    };
  }

  private spawnParticle(
    position: any,
    velocityX: number,
    velocityY: number,
    velocityZ: number,
    color: number,
    life: number,
    gravity: number,
    grow: number,
    kind: ParticleKind,
    scale: number,
  ): void {
    if (this.particles.length >= this.maxParticles) this.releaseParticle(0);
    const particle = this.acquireParticle();
    particle.kind = kind;
    particle.position.copy(position);
    particle.position.y += 0.12;
    particle.velocity.set(velocityX, velocityY, velocityZ);
    particle.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
    particle.spin.set((Math.random() - 0.5) * 9, (Math.random() - 0.5) * 9, (Math.random() - 0.5) * 9);
    particle.life = life;
    particle.maxLife = life;
    particle.gravity = gravity;
    particle.grow = grow;
    particle.baseScale = scale * (0.65 + Math.random() * 0.65);
    particle.color = color;
    this.particles.push(particle);
  }

  private releaseParticle(index: number): void {
    const particle = this.particles[index];
    const last = this.particles.pop();
    if (!particle || !last) return;
    if (index < this.particles.length) this.particles[index] = last;
    this.particlePool.push(particle);
  }

  private ring(position: any, color: number, scale: number, speed: number): void {
    if (this.rings.length >= this.maxRings) this.releaseRing(0);
    const effect = this.ringPool.pop() ?? this.createRingState();
    effect.position.copy(position);
    effect.position.y = Math.max(0.12, Number(position.y) || 0.12);
    effect.life = 0.42;
    effect.maxLife = 0.42;
    effect.speed = speed * scale;
    effect.baseScale = scale;
    effect.color = color;
    this.rings.push(effect);
  }

  private createRingState(): RingEffect {
    return {
      position: new THREE.Vector3(),
      life: 0,
      maxLife: 0,
      speed: 0,
      baseScale: 1,
      color: 0xffffff,
    };
  }

  private releaseRing(index: number): void {
    const effect = this.rings[index];
    const last = this.rings.pop();
    if (!effect || !last) return;
    if (index < this.rings.length) this.rings[index] = last;
    this.ringPool.push(effect);
  }

  private flash(position: any, color: number, intensity: number, life: number): void {
    if (this.flashes.length >= this.maxFlashes) this.releaseFlash(0);
    const effect = this.flashPool.pop() ?? this.createFlashState();
    effect.position.copy(position);
    effect.position.y += 0.3;
    effect.life = life;
    effect.maxLife = life;
    effect.baseScale = Math.max(0.55, intensity * 0.42);
    effect.color = color;
    this.flashes.push(effect);
  }

  private createFlashState(): FlashEffect {
    return {
      position: new THREE.Vector3(),
      life: 0,
      maxLife: 0,
      baseScale: 1,
      color: 0xffffff,
    };
  }

  private releaseFlash(index: number): void {
    const effect = this.flashes[index];
    const last = this.flashes.pop();
    if (!effect || !last) return;
    if (index < this.flashes.length) this.flashes[index] = last;
    this.flashPool.push(effect);
  }

  private syncInstances(): void {
    let shardCount = 0;
    let cubeCount = 0;
    for (const particle of this.particles) {
      const normalized = Math.max(0, particle.life / particle.maxLife);
      const fadeScale = Math.min(1, normalized * 4);
      const growth = 1 + particle.grow * (1 - normalized);
      this.dummy.position.copy(particle.position);
      this.dummy.rotation.set(particle.rotation.x, particle.rotation.y, particle.rotation.z);
      this.dummy.scale.setScalar(Math.max(0.001, particle.baseScale * fadeScale * growth));
      this.dummy.updateMatrix();
      const mesh = particle.kind === 'shard' ? this.shardInstances : this.cubeInstances;
      const index = particle.kind === 'shard' ? shardCount++ : cubeCount++;
      mesh.setMatrixAt(index, this.dummy.matrix);
      this.tempColor.setHex(particle.color);
      if (normalized < 0.3) this.tempColor.multiplyScalar(0.7 + normalized);
      mesh.setColorAt(index, this.tempColor);
    }
    this.commitInstances(this.shardInstances, shardCount);
    this.commitInstances(this.cubeInstances, cubeCount);

    let ringCount = 0;
    for (const effect of this.rings) {
      const normalized = Math.max(0, effect.life / effect.maxLife);
      const progress = 1 - normalized;
      const endScale = Math.min(1, normalized * 4);
      const size = (0.45 + progress * effect.speed) * endScale;
      this.dummy.position.copy(effect.position);
      this.dummy.rotation.set(-Math.PI / 2, 0, 0);
      this.dummy.scale.setScalar(Math.max(0.001, size));
      this.dummy.updateMatrix();
      this.ringInstances.setMatrixAt(ringCount, this.dummy.matrix);
      this.ringInstances.setColorAt(ringCount, this.tempColor.setHex(effect.color));
      ringCount += 1;
    }
    this.commitInstances(this.ringInstances, ringCount);

    let flashCount = 0;
    for (const effect of this.flashes) {
      const normalized = Math.max(0, effect.life / effect.maxLife);
      const progress = 1 - normalized;
      const pulse = Math.sin(Math.PI * Math.min(1, progress));
      const scale = effect.baseScale * (0.3 + pulse * 0.9) * Math.min(1, normalized * 5);
      this.dummy.position.copy(effect.position);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.scale.setScalar(Math.max(0.001, scale));
      this.dummy.updateMatrix();
      this.flashInstances.setMatrixAt(flashCount, this.dummy.matrix);
      this.flashInstances.setColorAt(flashCount, this.tempColor.setHex(effect.color));
      flashCount += 1;
    }
    this.commitInstances(this.flashInstances, flashCount);
  }

  private commitInstances(mesh: any, count: number): void {
    const countChanged = mesh.count !== count;
    mesh.count = count;
    if (count > 0 || countChanged) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }
}
